import React, { useEffect, useRef, useState } from "react";
import { FaArrowRight, FaWhatsapp, FaCheckCircle } from "react-icons/fa";
import Img1 from "../../Assets/Images/img1.png";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";
import { Link, useParams, useNavigate } from "react-router-dom";
import useScrollToTop from "../../hooks/useScrollToTop";

const ProductsHeroSection = () => {
  useScrollToTop();

  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Ref for main content (scroll target)
  const contentRef = useRef(null);

  // ✅ Fetch all products
  const getAllProducts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/products/all");
      setProducts(response?.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle active product when URL or product list changes
  useEffect(() => {
    if (products.length > 0) {
      if (id) {
        const found = products.find((prd) => prd._id === id);
        setActiveProduct(found || products[0]);
      } else {
        setActiveProduct(products[0]);
        navigate(`/our-products/${products[0]._id}`, { replace: true });
      }
    }
  }, [id, products, navigate]);

  useEffect(() => {
    getAllProducts();
  }, []);

  useEffect(() => {
    if (error) toast.error(error, { id: "error" });
  }, [error]);

  // ✅ Scroll to main content on small screens
  const handleProductClick = (prdId) => {
    navigate(`/our-products/${prdId}`);
    if (window.innerWidth < 1024) {
      // Only scroll on mobile/tablet
      setTimeout(() => {
        contentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200); // wait a little for content to update
    }
  };

  // ✅ Skeleton Loader for main content
  const ContentSkeleton = () => (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-1/2 bg-gray-300 rounded"></div>
      <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
      <div className="h-64 bg-gray-200 rounded"></div>
      <div className="h-6 w-1/3 bg-gray-300 rounded"></div>
      <div className="h-4 w-full bg-gray-200 rounded"></div>
      <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
    </div>
  );

  // ✅ Skeleton Loader for sidebar
  const SidebarSkeleton = () => (
    <div className="bg-white rounded-md shadow p-4 animate-pulse">
      <div className="h-8 w-1/2 bg-gray-300 rounded mb-4"></div>
      <ul className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <li key={i} className="h-10 bg-gray-200 rounded w-full"></li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="container mx-auto px-4 md:px-10 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar */}
        <div className="col-span-1 space-y-6">
          {loading ? (
            <SidebarSkeleton />
          ) : (
            <div className="bg-white rounded-md shadow p-4">
              <h3
                className="text-[#030B25] font-orbitron font-semibold capitalize
                text-[24px] sm:text-[26px] md:text-[28px] lg:text-[30px]
                leading-[120%] tracking-[0.5px] mb-4"
              >
                Our Products
              </h3>
              <ul className="space-y-2">
                {products.map((prd) => (
                  <li key={prd._id}>
                    <button
                      onClick={() => handleProductClick(prd._id)}
                      className={`w-full flex justify-between items-center px-4 py-3 rounded text-left ${
                        prd._id === activeProduct?._id
                          ? "bg-[#E8471B] text-white"
                          : "bg-gray-100 text-gray-800 hover:bg-orange-100"
                      }`}
                    >
                      {prd.name}
                      <FaArrowRight />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Call to Action */}
          <div className="bg-white rounded-md shadow p-4 text-center relative">
            <img
              src={Img1}
              alt="Support Person"
              className="w-full rounded-md"
            />
            <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#E8471B] w-12 h-12 flex items-center justify-center rounded-full text-white text-lg">
              <FaWhatsapp />
            </div>
            <p
              className="mt-12 font-orbitron font-semibold text-[#030B25] text-center capitalize
              text-[20px] sm:text-[22px] md:text-[24px]"
            >
              Need Any Service Or Product, Feel Free To Call Us
            </p>
            <button className="mt-4 bg-[#02081A] text-white px-6 py-2 rounded">
              <p className="font-quicksand font-semibold text-[14px] sm:text-[15px] md:text-[16px]">
                WhatsApp Us
              </p>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div ref={contentRef} className="col-span-2 space-y-8">
          {loading ? (
            <ContentSkeleton />
          ) : activeProduct ? (
            <>
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-800">
                  {activeProduct.name}
                </h1>
                <p className="mt-4 text-gray-600 text-sm sm:text-base md:text-lg">
                  {activeProduct.description}
                </p>
              </div>

              {activeProduct.images?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 p-3 bg-white">
                  <div className="flex items-center justify-center">
                    <img
                      src={activeProduct.images[0]}
                      alt={activeProduct.name}
                      className="rounded-md w-full object-cover border-2 shadow-md"
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={activeProduct.images[1]}
                      alt={activeProduct.name}
                      className="rounded-md w-full object-cover border-2 shadow-md"
                    />
                  </div>
                </div>
              )}

              {activeProduct.infoSection?.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base md:text-lg">
                    {section.content}
                  </p>
                  {section.points?.length > 0 && (
                    <ul className="space-y-2 text-gray-800 text-sm sm:text-base md:text-lg">
                      {section.points.map((point, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <FaCheckCircle className="text-orange-600" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </>
          ) : (
            <p className="text-gray-500">No product details found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsHeroSection;
